import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../services/api.service';
import { LoginRedirectService } from '../services/login-redirect.service';
import { ProjectService } from '../services/project.service';
import { WhiteLabelApplicationService } from '../services/white-label-application.service';
import { CurrentWhiteLabelApplication } from '../utils/current-white-label-application';

export const privateAppGuard: CanActivateFn = async (_route, state) => {
  const app = inject(CurrentWhiteLabelApplication);
  const apiService = inject(ApiService);
  const projectService = inject(ProjectService);
  const loginRedirectService = inject(LoginRedirectService);
  const whiteLabelApplicationService = inject(WhiteLabelApplicationService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformServer(platformId)) {
    return true;
  }

  await whiteLabelApplicationService.waitUntilReady();

  if (!app.isPrivate()) {
    return true;
  }

  if (!(await firstValueFrom(apiService.getAuthenticated()))) {
    await storeIntendedUrl(loginRedirectService, state.url);

    return router.parseUrl('/login');
  }

  if (!app.isProjectApp()) {
    return true;
  }

  try {
    const hasAccess = await projectService.hasAccessToPrivateProject(
      app.getProjectAppSlug()
    );

    return hasAccess ? true : router.parseUrl('/no-access');
  } catch {
    return false;
  }
};

async function storeIntendedUrl(
  loginRedirectService: LoginRedirectService,
  url: string
): Promise<void> {
  const segments = (url ?? '').split('?')[0].split('/').filter(Boolean);

  if (segments.length < 2) {
    return;
  }

  await loginRedirectService.storeLoginRedirectUrl(`/${segments.join('/')}`);
}
